/**
 * API client for the Kevin Data Integration extraction service.
 *
 * This client connects to the FastAPI backend for document extraction.
 * In development, it runs on localhost:8000.
 * In production, update EXTRACTION_API_URL to the deployed URL.
 */

// API base URL - update this when deploying
const EXTRACTION_API_URL = import.meta.env.VITE_EXTRACTION_API_URL || 'http://localhost:8000';

/**
 * Document status from the extraction API
 */
export type DocumentStatus = 'uploaded' | 'processing' | 'completed' | 'failed';

/**
 * Review status for a document
 */
export type ReviewStatus = 'pending_review' | 'approved' | 'rejected';

/**
 * Document metadata returned by the API
 */
export interface DocumentMetadata {
  id: string;
  filename: string;
  document_type: string | null;
  classification_confidence: 'high' | 'medium' | 'low' | null;
  classification_reasoning: string | null;
  status: DocumentStatus;
  review_status: ReviewStatus | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Response from upload endpoint
 */
export interface UploadResponse {
  document_id: string;
  filename: string;
  status: DocumentStatus;
  message: string;
}

/**
 * Response from extraction data endpoint
 */
export interface ExtractionDataResponse {
  document_id: string;
  document_type: string | null;
  status: DocumentStatus;
  data: Record<string, unknown> | null;
  error_message: string | null;
}

/**
 * Response from document list endpoint
 */
export interface DocumentListResponse {
  documents: DocumentMetadata[];
  total: number;
}

/**
 * Error thrown by API calls
 */
export class ExtractionAPIError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = 'ExtractionAPIError';
  }
}

/**
 * Error categories for user-friendly messaging
 */
export type ErrorCategory = 'network' | 'auth' | 'validation' | 'processing' | 'timeout' | 'unknown';

/**
 * Categorize an error for appropriate user messaging
 */
export function categorizeError(error: ExtractionAPIError): ErrorCategory {
  // Check for network issues first
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'network';
  if (error.status === 0) return 'network'; // Fetch failed entirely

  // HTTP status-based categorization
  if (error.status === 401 || error.status === 403) return 'auth';
  if (error.status === 400 || error.status === 422) return 'validation';
  if (error.status === 408) return 'timeout';
  if (error.status >= 500) return 'processing';

  return 'unknown';
}

/**
 * Get a user-friendly error message based on error category
 */
export function getFriendlyErrorMessage(category: ErrorCategory, detail?: string): string {
  switch (category) {
    case 'network':
      return 'Unable to connect to the server. Please check your internet connection.';
    case 'auth':
      return 'Your session has expired. Please refresh the page and try again.';
    case 'validation':
      return detail || 'The file could not be processed. Please check the format and try again.';
    case 'timeout':
      return 'Processing took too long. Please try again with a smaller file.';
    case 'processing':
      return 'A server error occurred. Our team has been notified.';
    default:
      return detail || 'Something went wrong. Please try again.';
  }
}

/**
 * Get both category and friendly message for an error
 */
export function getErrorInfo(error: ExtractionAPIError): { category: ErrorCategory; message: string } {
  const category = categorizeError(error);
  const message = getFriendlyErrorMessage(category, error.detail);
  return { category, message };
}

/**
 * Default request timeout in milliseconds (30 seconds)
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * Get the current Supabase session token for API auth
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Make a request to the extraction API with timeout
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<T> {
  const url = `${EXTRACTION_API_URL}${endpoint}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Inject auth token
  const token = await getAuthToken();
  const authHeaders: Record<string, string> = token
    ? { 'Authorization': `Bearer ${token}` }
    : {};

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      let detail: string | undefined;
      try {
        const errorData = await response.json();
        detail = errorData.detail || errorData.message;
      } catch {
        // Ignore JSON parse errors
      }
      throw new ExtractionAPIError(
        `API request failed: ${response.statusText}`,
        response.status,
        detail
      );
    }

    return response.json();
  } catch (error) {
    // Handle abort/timeout
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExtractionAPIError(
        'Request timed out',
        408,
        'The request took too long to complete. Please try again.'
      );
    }
    // Handle network errors (fetch failed entirely)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ExtractionAPIError(
        'Network error',
        0,
        'Unable to connect to the server. Please check your internet connection.'
      );
    }
    // Re-throw ExtractionAPIError as-is
    if (error instanceof ExtractionAPIError) {
      throw error;
    }
    // Wrap unknown errors
    throw new ExtractionAPIError(
      'Request failed',
      0,
      error instanceof Error ? error.message : 'An unknown error occurred'
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Check if the extraction API is healthy
 */
export async function checkHealth(): Promise<{ status: string; mode: string }> {
  return apiRequest('/health');
}

/**
 * Upload a document for extraction
 *
 * @param file - PDF file to upload
 * @param clientId - Client ID for multi-tenancy
 * @param orgId - Optional organization ID
 * @param familyId - Optional family ID
 */
export async function uploadDocument(
  file: File,
  clientId: string,
  orgId?: string,
  familyId?: string
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);
  if (orgId) formData.append('org_id', orgId);
  if (familyId) formData.append('family_id', familyId);

  return apiRequest('/documents/upload', {
    method: 'POST',
    body: formData,
  });
}

/**
 * Trigger extraction for an uploaded document
 *
 * @param documentId - Document ID returned from upload
 * @param force - Force re-extraction even if already completed
 */
export async function triggerExtraction(
  documentId: string,
  force: boolean = false
): Promise<{ message: string; status: string }> {
  return apiRequest(`/documents/${documentId}/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ force }),
  });
}

/**
 * Get the status of a document
 *
 * @param documentId - Document ID
 */
export async function getDocumentStatus(
  documentId: string
): Promise<{ document: DocumentMetadata }> {
  return apiRequest(`/documents/${documentId}/status`);
}

/**
 * Get extracted data for a document
 *
 * @param documentId - Document ID
 */
export async function getExtractionData(
  documentId: string
): Promise<ExtractionDataResponse> {
  return apiRequest(`/documents/${documentId}/data`);
}

/**
 * Options for listing documents
 */
export interface ListDocumentsOptions {
  clientId?: string;
  status?: DocumentStatus;
  reviewStatus?: 'pending_review' | 'approved' | 'rejected';
  documentType?: string;
}

/**
 * List all documents with optional filters
 *
 * @param options - Filter options
 */
export async function listDocuments(
  options: ListDocumentsOptions = {}
): Promise<DocumentListResponse> {
  const params = new URLSearchParams();
  if (options.clientId) params.append('client_id', options.clientId);
  if (options.status) params.append('status', options.status);
  if (options.reviewStatus) params.append('review_status', options.reviewStatus);
  if (options.documentType) params.append('document_type', options.documentType);

  const queryString = params.toString();
  return apiRequest(`/documents${queryString ? `?${queryString}` : ''}`);
}

/**
 * Poll for extraction completion
 *
 * @param documentId - Document ID to poll
 * @param onStatusChange - Callback when status changes
 * @param intervalMs - Polling interval in milliseconds
 * @param timeoutMs - Maximum time to poll before giving up
 * @returns Final extraction data
 */
export async function pollForCompletion(
  documentId: string,
  onStatusChange?: (status: DocumentStatus, document: DocumentMetadata) => void,
  intervalMs: number = 2000,
  timeoutMs: number = 120000
): Promise<ExtractionDataResponse> {
  const startTime = Date.now();

  while (true) {
    const { document } = await getDocumentStatus(documentId);

    if (onStatusChange) {
      onStatusChange(document.status, document);
    }

    if (document.status === 'completed' || document.status === 'failed') {
      return getExtractionData(documentId);
    }

    if (Date.now() - startTime > timeoutMs) {
      throw new ExtractionAPIError(
        'Extraction timeout',
        408,
        'Extraction took too long to complete'
      );
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
}

/**
 * Upload and extract a document in one call
 *
 * @param file - PDF file to upload
 * @param clientId - Client ID for multi-tenancy
 * @param options - Additional options
 * @returns Extraction result
 */
export async function uploadAndExtract(
  file: File,
  clientId: string,
  options: {
    orgId?: string;
    familyId?: string;
    onStatusChange?: (status: DocumentStatus, document: DocumentMetadata) => void;
    pollIntervalMs?: number;
    timeoutMs?: number;
  } = {}
): Promise<ExtractionDataResponse> {
  // Upload
  const uploadResult = await uploadDocument(
    file,
    clientId,
    options.orgId,
    options.familyId
  );

  // Trigger extraction
  await triggerExtraction(uploadResult.document_id);

  // Poll for completion
  return pollForCompletion(
    uploadResult.document_id,
    options.onStatusChange,
    options.pollIntervalMs,
    options.timeoutMs
  );
}

// ============================================================================
// Review & Approval API Methods
// ============================================================================

/**
 * Provenance info for a single field
 */
export interface FieldProvenance {
  page: number;
  text: string;
}

/**
 * Response from provenance endpoint
 */
export interface ProvenanceResponse {
  document_id: string;
  provenance: Record<string, FieldProvenance | Record<string, FieldProvenance>>;
}

/**
 * Classification info for a document
 */
export interface ClassificationInfo {
  type: string;
  confidence: 'high' | 'medium' | 'low' | 'unknown';
  reasoning: string;
}

/**
 * Response from document data endpoint with classification
 */
export interface DocumentDataResponse {
  document_id: string;
  document_type: string | null;
  classification_confidence: string | null;
  status: DocumentStatus;
  data: Record<string, unknown> | null;
  provenance: Record<string, FieldProvenance | Record<string, FieldProvenance>> | null;
  classification: ClassificationInfo | null;
  error_message: string | null;
}

/**
 * Download the PDF file for a document
 *
 * @param documentId - Document ID
 * @returns Blob of the PDF file
 */
export async function getDocumentFile(documentId: string): Promise<Blob> {
  const url = `${EXTRACTION_API_URL}/documents/${documentId}/file`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const token = await getAuthToken();
  const headers: Record<string, string> = token
    ? { 'Authorization': `Bearer ${token}` }
    : {};

  try {
    const response = await fetch(url, { signal: controller.signal, headers });

    if (!response.ok) {
      throw new ExtractionAPIError(
        `Failed to download file: ${response.statusText}`,
        response.status
      );
    }

    return response.blob();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExtractionAPIError('Request timed out', 408);
    }
    if (error instanceof ExtractionAPIError) {
      throw error;
    }
    throw new ExtractionAPIError(
      'Failed to download file',
      0,
      error instanceof Error ? error.message : 'Unknown error'
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get provenance data for a document (source text and page numbers for each field)
 *
 * @param documentId - Document ID
 */
export async function getProvenance(documentId: string): Promise<ProvenanceResponse> {
  return apiRequest(`/documents/${documentId}/provenance`);
}

/**
 * Get full document data including provenance and classification
 *
 * @param documentId - Document ID
 */
export async function getDocumentData(documentId: string): Promise<DocumentDataResponse> {
  return apiRequest(`/documents/${documentId}/data`);
}

/**
 * Update extracted data for a document
 *
 * @param documentId - Document ID
 * @param data - Updated field values
 */
export async function updateDocumentData(
  documentId: string,
  data: Record<string, unknown>
): Promise<{ message: string }> {
  return apiRequest(`/documents/${documentId}/data`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
}

/**
 * Approve a document's extracted data
 *
 * @param documentId - Document ID
 * @param reviewer - Name/email of the reviewer
 */
export async function approveDocument(
  documentId: string,
  reviewer: string
): Promise<{ message: string }> {
  return apiRequest(`/documents/${documentId}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reviewer }),
  });
}

/**
 * Reject a document's extracted data
 *
 * @param documentId - Document ID
 * @param reason - Optional reason for rejection
 */
export async function rejectDocument(
  documentId: string,
  reason?: string
): Promise<{ message: string }> {
  return apiRequest(`/documents/${documentId}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reason }),
  });
}

// ============================================
// Portfolio Investment Types & Methods
// ============================================

/**
 * A single portfolio company position extracted from a quarterly financial statement
 */
export interface PortfolioInvestment {
  id?: string;
  document_id?: string;
  fund_name: string;
  statement_date: string;
  investment_name: string;
  investment_type: string | null;
  industry_sector: string | null;
  cost_basis: number | null;
  fair_value: number | null;
  unrealized_gain_loss: number | null;
  ownership_percentage: number | null;
  shares_units: number | null;
  investment_stage: string | null;
  geographic_region: string | null;
  initial_investment_date: string | null;
  notes: string | null;
  source_page?: number | null;
}

export interface PortfolioListResponse {
  investments: PortfolioInvestment[];
  total: number;
}

/**
 * Get portfolio company investments for a specific document
 */
export async function getDocumentPortfolio(
  documentId: string
): Promise<PortfolioListResponse> {
  return apiRequest(`/documents/${documentId}/portfolio`);
}

/**
 * Get all portfolio company investments for a fund (across all statements)
 */
export async function getFundPortfolio(
  fundName: string
): Promise<PortfolioListResponse> {
  return apiRequest(`/portfolio?fund_name=${encodeURIComponent(fundName)}`);
}

/**
 * Update portfolio investments for a document
 *
 * @param documentId - Document ID
 * @param investments - Full list of investments (replaces existing)
 * @param editor - Editor identifier (user ID or email)
 * @param notes - Optional edit notes
 */
export async function updateDocumentPortfolio(
  documentId: string,
  investments: PortfolioInvestment[],
  editor: string,
  notes?: string
): Promise<PortfolioListResponse> {
  return apiRequest(`/documents/${documentId}/portfolio`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ investments, editor, notes }),
  });
}
